import { useRef } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { useIsSwitching } from '@/features/chats/stores';
import { useChatSuggestions } from '../../model/useChatSuggestions';
import { useSuggestionsHidden } from '../../model/suggestionsVisibility/suggestionsVisibility.store';
import { ChatSuggestions } from '../ChatSuggestions/ChatSuggestions';
import { ComposerAttachments } from '../ComposerAttachments';
import { ChatInputExtensionsProps } from './ChatInputExtensions.types';

export const ChatInputExtensions = ({
  isNewChat,
  onSend,
  waiting,
  files,
  handleRemoveFile,
  onHasContentChange,
  projectContextItems = [],
  onRemoveProjectContextItem,
  top,
}: ChatInputExtensionsProps) => {
  const isAgentSwitching = useIsSwitching();

  const { isSuggestionsLoading, suggestionsToShow, handleStarterClick } = useChatSuggestions({
    onSend,
  });
  const [suggestionsHidden, setSuggestionsHidden] = useSuggestionsHidden();

  // Hide chat suggestions during agent switching for cleaner UX
  const shouldShowChatSuggestionsSafe =
    !top &&
    !suggestionsHidden &&
    (isNewChat || !isAgentSwitching) &&
    (isSuggestionsLoading || suggestionsToShow.length > 0);

  const hasFiles = files.length > 0;
  const hasProjectContext = projectContextItems.length > 0;
  const hasAttachments = hasFiles || hasProjectContext;
  const hasContent = Boolean(top) || shouldShowChatSuggestionsSafe || hasAttachments;

  // Notify parent when the derived `hasContent` value transitions between
  // true/false. This used to run via `useEffect([hasContent, onHasContentChange])`.
  // Refactored to derived-during-render + `queueMicrotask` (no-use-effect skill,
  // Rule 1 + Rule 3): compute the transition here, defer the parent notification
  // out of our own render so React doesn't warn about setState-during-render.
  const lastNotifiedRef = useRef<boolean | null>(null);
  const onHasContentChangeRef = useRef(onHasContentChange);
  onHasContentChangeRef.current = onHasContentChange;
  if (lastNotifiedRef.current !== hasContent) {
    lastNotifiedRef.current = hasContent;
    queueMicrotask(() => {
      onHasContentChangeRef.current?.(hasContent);
    });
  }

  return (
    <AnimatePresence>
      {hasContent && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          style={{ overflow: 'hidden', width: '100%' }}
        >
          <div className="w-full">
            {top}
            <AnimatePresence>
              {shouldShowChatSuggestionsSafe && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                >
                  <ChatSuggestions
                    data-testid="chat-suggestions"
                    isLoading={isSuggestionsLoading}
                    suggestions={suggestionsToShow}
                    handleStarterClick={handleStarterClick}
                    disabled={waiting}
                    onHide={() => setSuggestionsHidden(true)}
                  />
                </motion.div>
              )}
            </AnimatePresence>
            <AnimatePresence>
              {hasAttachments && (
                <motion.div
                  className="border-b border-border-lighter"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                >
                  {/* Files and project-context chips share ONE row + animation. */}
                  <ComposerAttachments
                    files={files}
                    onRemoveFile={handleRemoveFile}
                    projectContextItems={projectContextItems}
                    onRemoveProjectContextItem={onRemoveProjectContextItem ?? (() => undefined)}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
